import React, { useState, useEffect, useLayoutEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Container, Alert } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import './../styles/App.css';

import AppNavbar from './AppNavbar';
import ContentLibrary from './ContentLibrary';
import UserStats from './UserStats';
import Settings from './Settings';
import LoadingModal from './LoadingModal';
import LanguageSelector from './LanguageSelector';
import dataService from '../services/dataService';
import aiServiceFactory from '../services/aiServiceFactory';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../utils/i18n';
import { authUrl } from '../utils/apiUrl';


// Función para obtener la pestaña desde el hash de la URL (fuera del componente para evitar problemas)
const getTabFromHash = () => {
  if (typeof window === 'undefined') return 'recommendations';
  const hash = window.location.hash.replace('#', '');
  const validTabs = ['recommendations', 'history', 'stats', 'statistics', 'settings'];
  if (validTabs.includes(hash)) {
    if (hash === 'statistics') return 'stats';
    if (hash === 'history') return 'recommendations';
    return hash;
  }
  return 'recommendations'; // Valor por defecto
};

function Dashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { language } = useLanguage();
  const initialSearchFromPost = searchParams.get('search') || '';
  const initialCategoryFromPost = searchParams.get('category') || '';

  const [activeTab, setActiveTab] = useState(() => getTabFromHash());
  const [userPreferences, setUserPreferences] = useState(null);
  const [userStats, setUserStats] = useState(null);
  const [aiServiceStatus, setAiServiceStatus] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [recommendationLoading, setRecommendationLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [loadingError, setLoadingError] = useState(false);
  const [isSuperUser, setIsSuperUser] = useState(false);

  // Al cambiar de usuario: limpiar caché y resetear servicio de IA para que use el token de este usuario (BD)
  useLayoutEffect(() => {
    if (user?.id) {
      dataService.clearCache();
      aiServiceFactory.resetService();
    }
  }, [user?.id]);

  // Sincronizar hash de URL con la pestaña activa
  useEffect(() => {
    // Leer hash inicial al cargar
    const hash = window.location.hash.replace('#', '');
    if (hash) {
      const tab = getTabFromHash();
      if (tab !== activeTab) {
        setActiveTab(tab);
        return; // No actualizar hash aquí, el siguiente efecto lo hará
      }
    }
    // Si no hay hash, establecer el hash según la pestaña activa
    const hashValue = activeTab === 'stats' ? 'statistics' : activeTab;
    if (window.location.hash.replace('#', '') !== hashValue) {
      window.location.hash = hashValue;
    }
  }, []); // Solo al montar

  // Actualizar hash cuando cambia activeTab (library page owns #recommendations vs #history)
  useEffect(() => {
    if (activeTab === 'recommendations' || activeTab === 'history') {
      return;
    }
    const hashValue = activeTab === 'stats' ? 'statistics' : activeTab;
    const currentHash = window.location.hash.replace('#', '');
    if (currentHash !== hashValue) {
      window.location.hash = hashValue;
    }
  }, [activeTab]);

  // Escuchar cambios en el hash (navegación directa o botón atrás/adelante)
  useEffect(() => {
    const handleHashChange = () => {
      const tab = getTabFromHash();
      if (tab !== activeTab) {
        setActiveTab(tab);
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [activeTab]);

  useEffect(() => {
    if (!user?.id) return;
    initializeApp();
    const handleRecommendationLoading = (event) => {
      const { isLoading, message, isError } = event.detail;
      setRecommendationLoading(isLoading);
      setLoadingMessage(message || '');
      setLoadingError(isError || false);
    };
    window.addEventListener('recommendationLoading', handleRecommendationLoading);
    return () => window.removeEventListener('recommendationLoading', handleRecommendationLoading);
  }, [user?.id]);

  const initializeApp = async () => {
    try {
      setLoading(true);
      setError(null);
      dataService.clearCache();
      const preferences = await dataService.getUserPreferences();
      setUserPreferences(preferences);
      const stats = await dataService.getUserStats();
      setUserStats(stats);
      
      // Verificar si es superusuario
      try {
        const response = await fetch(authUrl('/auth/me'), { credentials: 'include' });
        if (response.ok) {
          const userData = await response.json();
          setIsSuperUser(userData.role === 'superuser');
        }
      } catch (e) {
        setIsSuperUser(false);
      }
      
      try {
        const aiService = await aiServiceFactory.createService();
        const isAvailable = await aiService.isAvailable();
        setAiServiceStatus(isAvailable);
        if (!isAvailable) {
          setError(t('common.aiServiceNotAvailable', language));
        }
      } catch (e) {
        setAiServiceStatus(false);
        setError(t('common.aiServiceNotAvailable', language));
      }

      console.warn('[MisKosas ratings] al abrir /app se ejecutará repairLegacyRatingsNormalization (ver consola)');
      dataService.repairLegacyRatingsNormalization().catch((err) => {
        console.warn('[MisKosas ratings] repair failed', err);
      });
    } catch (err) {
      setError(t('common.errorLoadingData', language));
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsWatched = async (content, userRating) => {
    try {
      const success = await dataService.markAsWatched(content, userRating);
      if (success) {
        const updatedStats = await dataService.getUserStats();
        setUserStats(updatedStats);
        setError(null);
        return true;
      }
      setError(t('common.errorMarkingWatched', language));
      return false;
    } catch (err) {
      setError(t('common.errorMarkingWatched', language));
      return false;
    }
  };

  const handleUpdatePreferences = async (newPreferences) => {
    try {
      const success = await dataService.updateUserPreferences(newPreferences);
      if (success) {
        setUserPreferences(newPreferences);
        setError(null);
        return true;
      }
      setError(t('common.errorUpdatingPreferences', language));
      return false;
    } catch (err) {
      setError(t('common.errorUpdatingPreferences', language));
      return false;
    }
  };

  const handleProviderChange = async () => {
    try {
      aiServiceFactory.resetService();
      const aiService = await aiServiceFactory.createService();
      const isAvailable = await aiService.isAvailable();
      setAiServiceStatus(isAvailable);
      setError(isAvailable ? null : t('common.aiServiceNotAvailable', language));
    } catch (err) {
      setAiServiceStatus(false);
    }
  };

  const handleNavSelect = (tab) => {
    setActiveTab(tab);
    // El hash se actualizará automáticamente por el useEffect
  };

  const handleLogout = () => {
    dataService.clearCache();
    dataService.clearLocalStorage();
    aiServiceFactory.resetService();
    onLogout();
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading', language)}</span>
          </div>
          <p className="mt-3">{t('recommendations.loading', language)}</p>
        </div>
      );
    }
    switch (activeTab) {
      case 'history':
      case 'recommendations':
        return (
          <ContentLibrary
            key={user?.id}
            userPreferences={userPreferences}
            onMarkAsWatched={handleMarkAsWatched}
            aiServiceStatus={aiServiceStatus}
            initialSearch={initialSearchFromPost}
            initialCategory={initialCategoryFromPost}
          />
        );
      case 'stats':
        return <UserStats key={user?.id} stats={userStats} />;
      case 'settings':
        return (
          <Settings
            preferences={userPreferences}
            onUpdatePreferences={handleUpdatePreferences}
            aiServiceStatus={aiServiceStatus}
            onProviderChange={handleProviderChange}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="App miskosas-app-shell">
      <AppNavbar
        user={user}
        onLogout={handleLogout}
        language={language}
        isSuperUser={isSuperUser}
        aiServiceStatus={aiServiceStatus}
        activeSection={activeTab}
        onNavSelect={handleNavSelect}
      />

      <Container>
        {error && (
          <Alert variant="warning" dismissible onClose={() => setError(null)}>
            <div>
              {error}
              {error === t('common.aiServiceNotAvailable', language) && (
                <div className="mt-2">
                  <Link to="/groq-tutorial" className="alert-link">
                    {t('common.learnHowToConfigure', language)}
                  </Link>
                </div>
              )}
            </div>
          </Alert>
        )}
        {renderContent()}
      </Container>

      <LoadingModal
        isVisible={recommendationLoading}
        title={loadingError 
          ? t('common.errorGeneratingRecommendations', language)
          : t('common.generatingRecommendations', language)
        }
        message={loadingMessage}
        isError={loadingError}
      />
    </div>
  );
}

export default Dashboard;
